import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getAmplitudeInstance } from '@alltrails/shared/utils/amplitudeHelpers';
import { OptimizelyExperiment, OptimizelyProvider, OptimizelyVariation } from '@optimizely/react-sdk';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { ProPageStrings } from 'react_components/mixins/shared/string_constants_mixin';
import CheckCircleIcon from 'images/pro/greenCheck-filled.svg';
import { initializeOptimizely, trackOptimizelyEvent } from 'utils/optimizely_helpers';
import { SIGN_UP_PAGE_VIEWED, TRAIL_PAGE_VIEW_LIMIT_REACHED } from 'utils/constants/AmplitudeEvent';
import BulletList from 'components/shared/text/BulletList';
import Wrapper from '../Wrapper';
import * as baseStyles from '../../../styles/baseStyles.module.scss';
import * as styles from './styles/styles.module.scss';
import RegistrationForm from '../RegistrationForm';

const getText = isProPage => {
  const { registrationText, loginForm, formText, altLoginsText } = ProPageStrings;
  let text = {
    ...registrationText,
    ...loginForm,
    ...formText,
    ...altLoginsText
  };

  if (isProPage) {
    text = { ...text, ...ProPageStrings };
  }

  return text;
};

const handleOptimizelyEvent = (optimizelyInstance, optimizelyUserId, eventType, eventOutcome, eventAttributes) => {
  if (optimizelyInstance) {
    trackOptimizelyEvent(optimizelyInstance, optimizelyUserId, eventType, eventOutcome, eventAttributes);
  }
};

const FormFooter = ({ text }) => {
  return (
    <div className={baseStyles.info}>
      {text.privacyPolicyLead}{' '}
      <a className={baseStyles.route} href="/terms">
        {text.termsLink}
      </a>{' '}
      {text.and}{' '}
      <a className={baseStyles.route} href="/privacy">
        {text.privacy}
      </a>
      .
    </div>
  );
};

const OptimizelyMockSignupPageTest = ({ optimizelyInstance }) => {
  if (!optimizelyInstance) {
    return null;
  }

  const featuresList = ['Keep track of your favorite trails', 'Record and share your activity', 'Create and share custom maps'];

  return (
    <OptimizelyProvider optimizely={optimizelyInstance} isServerSide>
      <OptimizelyExperiment experiment="mock_signup_page_test">
        <OptimizelyVariation variation="variation_description">
          <BulletList
            bullet={<img alt="check-circle-icon" src={CheckCircleIcon} style={{ color: '#428a13' }} />}
            classes={styles.bulletList}
            list={featuresList}
          />
        </OptimizelyVariation>
        <OptimizelyVariation variation="variation_control" />
        <OptimizelyVariation default />
      </OptimizelyExperiment>
    </OptimizelyProvider>
  );
};

const SignUpForm = ({
  isProPage,
  mobileBrowser,
  optimizelySettings,
  countryId,
  handleRegistrationSuccess,
  redirectTo,
  handleLoginClick,
  enableRecaptcha,
  facebookAppId,
  referralLink
}) => {
  const languageRegionCode = useLanguageRegionCode();
  const [optimizelyInstance, updateOptimizelyInstance] = useState(null);
  const [optimizelyUserId, updateOptimizelyUserId] = useState(null);
  useEffect(() => {
    const [_optimizelyInstance, _optimizelyUserId] = initializeOptimizely(optimizelySettings);
    if (_optimizelyInstance) {
      const optimizelyAttributes = { locale: languageRegionCode, mobileBrowser };
      _optimizelyInstance.setUser({ id: _optimizelyUserId, attributes: optimizelyAttributes });
      updateOptimizelyInstance(_optimizelyInstance);
      updateOptimizelyUserId(_optimizelyUserId);
    }
  }, []);

  useEffect(() => {
    getAmplitudeInstance().logEvent(SIGN_UP_PAGE_VIEWED);

    const params = new URLSearchParams(window.location.search);

    if (params.has('limit') && params.has('trail_id')) {
      getAmplitudeInstance().logEvent(TRAIL_PAGE_VIEW_LIMIT_REACHED, { limit: params.get('limit'), 'trail id': params.get('trail_id') });
      params.delete('limit');
      params.delete('trail_id');
      const search = params.toString();
      window.history.replaceState(null, null, window.location.pathname + (search !== '' ? `?${search}` : ''));
    }
  }, []);

  const text = getText(isProPage);
  const formFooter = <FormFooter text={text} />;

  return (
    <Wrapper text={text} foot={formFooter}>
      <OptimizelyMockSignupPageTest optimizelyInstance={optimizelyInstance} />
      <RegistrationForm
        text={text}
        countryId={countryId}
        handleRegistrationSuccess={handleRegistrationSuccess}
        handleLoginClick={handleLoginClick}
        enableRecaptcha={enableRecaptcha}
        facebookAppId={facebookAppId}
        redirectTo={redirectTo}
        handleOptimizelyEvent={(eventType, eventOutcome, eventAttributes) =>
          handleOptimizelyEvent(optimizelyInstance, optimizelyUserId, eventType, eventOutcome, eventAttributes)
        }
        referralLink={referralLink}
      />
    </Wrapper>
  );
};

SignUpForm.propTypes = {
  isProPage: PropTypes.bool,
  mobileBrowser: PropTypes.bool,
  optimizelySettings: PropTypes.object,
  countryId: PropTypes.number.isRequired,
  handleRegistrationSuccess: PropTypes.func,
  redirectTo: PropTypes.string,
  handleLoginClick: PropTypes.func,
  enableRecaptcha: PropTypes.bool.isRequired,
  facebookAppId: PropTypes.number.isRequired,
  referralLink: PropTypes.string
};

SignUpForm.defaultProps = {
  isProPage: false,
  handleRegistrationSuccess: () => {},
  handleLoginClick: null,
  redirectTo: null,
  mobileBrowser: null,
  optimizelySettings: null,
  referralLink: null
};

FormFooter.propTypes = {
  text: PropTypes.shape({
    privacyPolicyLead: PropTypes.string,
    termsLink: PropTypes.string,
    privacy: PropTypes.string
  })
};

FormFooter.defaultProps = {
  text: {
    privacyPolicyLead: 'Privacy Policy',
    termsLink: '/terms',
    privacy: 'Privacy'
  }
};

export default SignUpForm;
